import { useTestRun } from "@/hooks/useApi";
import type { TestRunResponse } from "@/types/api";
import {
  type StagedResource,
  suiteCaseFromTest,
  type TestAssertionDraft,
  usePipelineStaging,
} from "./pipelineStaging";
import {
  clearRunId,
  type ScriptRunOutput,
  suiteRunOutput,
  testRunOutput,
} from "./scriptRun";
import { PASSTHROUGH_SCRIPT, parseInputJson } from "./useGenerateSuiteTests";
import { pipelineScripts } from "./useScriptRun";

// The test rows' Run and the suite row's Run all: cases go through POST
// /transform/test/run (the old /studio's flow) against the staged
// transforms — or the identity pipeline when none are staged, the wand's
// rule — and the verdict lands in the Output panel like a script run
// (the view builders live in scriptRun.ts). The shared runningId
// disables only the clicked button while its request is in flight.

/** The runningId used while the whole suite runs (vs a test row id). */
export const SUITE_RUN_ID = "suite-tests";

// The rows the editor would show for a row: a kept working copy wins over
// the saved lines (an untitled draft only has the copy).
function rowsFor(
  id: string,
  testDrafts: Record<string, TestAssertionDraft[]>,
  testAssertions: Record<string, TestAssertionDraft[]>,
): TestAssertionDraft[] {
  return testDrafts[id] ?? testAssertions[id] ?? [];
}

// A caught run's Output-panel face: the failure text is the error's own
// message when it has one, the generic fallback otherwise.
function runFailureOutput(label: string, error: unknown): ScriptRunOutput {
  return {
    scriptName: label,
    success: false,
    text: error instanceof Error ? error.message : "Failed to run test",
  };
}

// The wand's identity-pipeline rule: an empty resolved chain (nothing
// staged) still needs a script to send, so it falls back to the
// passthrough transform.
function scriptsToRun(resolved: string[]): string[] {
  return resolved.length > 0 ? resolved : [PASSTHROUGH_SCRIPT];
}

export function useRunSuiteTest() {
  const {
    transforms,
    scriptDrafts,
    savedScripts,
    inputDocument,
    tests,
    stagedSuite,
    testAssertions,
    testDrafts,
    strictModeOf,
    showScriptRunOutput,
    scriptRunningId,
    setScriptRunningId,
  } = usePipelineStaging();
  const { mutateAsync: runTests } = useTestRun();

  const rowsOf = (id: string) => rowsFor(id, testDrafts, testAssertions);

  const execute = async (
    runId: string,
    label: string,
    cases: ReturnType<typeof suiteCaseFromTest>[],
    render: (item: TestRunResponse["item"]) => ScriptRunOutput,
  ) => {
    if (scriptRunningId === runId) return;
    setScriptRunningId(runId);
    try {
      const resolved = await pipelineScripts(
        transforms,
        scriptDrafts,
        savedScripts,
      );
      const response = await runTests({
        scripts: scriptsToRun(resolved),
        input: parseInputJson(inputDocument),
        cases,
      });
      showScriptRunOutput(render(response?.item));
    } catch (error) {
      showScriptRunOutput(runFailureOutput(label, error));
    } finally {
      setScriptRunningId(clearRunId(runId));
    }
  };

  // The sent case rides into the Output view builders too: the engine
  // reports failed assertions only, so the passing lines can only come
  // from what the request carried.
  const run = (test: StagedResource) => {
    const sent = suiteCaseFromTest(
      test.name,
      rowsOf(test.id),
      strictModeOf(test.id),
    );
    return execute(test.id, test.name, [sent], (item) =>
      testRunOutput(test.name, item?.results?.[0], sent),
    );
  };

  // Run all: every row of the group — saved cases and untitled drafts
  // alike — as ONE request, in row order.
  const runAll = () => {
    if (!stagedSuite || tests.length === 0) return;
    const name = stagedSuite.name;
    const sentCases = tests.map((t) =>
      suiteCaseFromTest(t.name, rowsOf(t.id), strictModeOf(t.id)),
    );
    return execute(SUITE_RUN_ID, name, sentCases, (item) =>
      suiteRunOutput(name, item, sentCases),
    );
  };

  return { run, runAll, runningId: scriptRunningId };
}
